import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { GenericSortPaginationRequest } from '../../interfaces/services';
import { NetworkAreas } from '../../interfaces/services/areas';
import { ApiStatus } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { formatHealthFilters } from '../../utils/stateHelpers';

interface AreaHealthState {
    networkAreaHealthApiStatus: ApiStatus;
    networkAreaHealthApiParams: GenericSortPaginationRequest;
    networkAreaHealth: NetworkAreas;
    bottomBalanceAreasApiStatus: ApiStatus;
    bottomBalanceAreas: NetworkAreas;
    topBalanceAreasApiStatus: ApiStatus;
    topBalanceAreas: NetworkAreas;
    bottomDensityAreasApiStatus: ApiStatus;
    bottomDensityAreas: NetworkAreas;
    topDensityAreasApiStatus: ApiStatus;
    topDensityAreas: NetworkAreas;
    bottomEmptyMilesAreasApiStatus: ApiStatus;
    bottomEmptyMilesAreas: NetworkAreas;
}

const initialAreas = {
    areas_count: 0,
    areas_health: {
        area_daily_balance_grade: null,
        area_daily_density_grade: null,
        area_empty_miles_percentage_grade: null,
    },
    areas: [],
    page_number: 0,
    size: 0,
    pages: 0,
};

const initialState: AreaHealthState = {
    networkAreaHealthApiStatus: ApiStatus.Idle,
    networkAreaHealthApiParams: {
        pageNumber: 1,
        pageSize: 100,
        sortBy: [
            { field: 'service_area_health_grade', sort: 'asc' },
            { field: 'average_daily_balance_amount', sort: 'asc' },
            { field: 'average_daily_density_amount', sort: 'desc' },
            { field: 'average_empty_miles_percentage_amount', sort: 'asc' },
        ],
    },
    networkAreaHealth: initialAreas,
    bottomBalanceAreasApiStatus: ApiStatus.Idle,
    bottomBalanceAreas: initialAreas,
    topBalanceAreasApiStatus: ApiStatus.Idle,
    topBalanceAreas: initialAreas,
    bottomDensityAreasApiStatus: ApiStatus.Idle,
    bottomDensityAreas: initialAreas,
    topDensityAreasApiStatus: ApiStatus.Idle,
    topDensityAreas: initialAreas,
    bottomEmptyMilesAreasApiStatus: ApiStatus.Idle,
    bottomEmptyMilesAreas: initialAreas,
};

export const fetchNetworkAreaHealth = createAsyncThunk(
    'areaHealth/fetchNetworkAreaHealth',
    async ({
        pageNumber,
        pageSize,
        sortBy,
    }: GenericSortPaginationRequest) => {
        try {
            const sortByList = sortBy.map((sortByObject) => {
                return `${sortByObject.sort === 'asc' ? '+' : '-'}${sortByObject.field}`;
            });

            const urlParams = new URLSearchParams();
            if (sortByList.length > 0) {
                urlParams.append('sort_by', sortByList.toString());
            }
            urlParams.append('page_number', pageNumber.toString());
            urlParams.append('size', pageSize.toString());

            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/areas?${urlParams}`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkAreas>;

            return data;
        } catch (error) {
            console.error('Unable to fetch network area health', error);
            toast.error('Unable to fetch network area health');
            throw error;
        }
    }
);

export const fetchBottomBalanceAreas = createAsyncThunk(
    'areaHealth/fetchBottomBalanceAreas',
    async () => {
        const urlParams = new URLSearchParams();
        urlParams.append('sort_by', '-average_daily_balance_absolute_amount');
        urlParams.append('page_number', '1');
        urlParams.append('size', '5');

        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/areas?${urlParams}`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkAreas>;

            return data;
        } catch (error) {
            console.error('Unable to fetch bottom balance areas', error);
            toast.error('Unable to fetch bottom balance areas');
            throw error;
        }
    }
);

export const fetchTopBalanceAreas = createAsyncThunk(
    'areaHealth/fetchTopBalanceAreas',
    async () => {
        const urlParams = new URLSearchParams();
        urlParams.append('sort_by', '+average_daily_balance_absolute_amount');
        urlParams.append('page_number', '1');
        urlParams.append('size', '5');

        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/areas?${urlParams}`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkAreas>;

            return data;
        } catch (error) {
            console.error('Unable to fetch top balance areas', error);
            toast.error('Unable to fetch top balance areas');
            throw error;
        }
    }
);

export const fetchBottomDensityAreas = createAsyncThunk(
    'areaHealth/fetchBottomDensityAreas',
    async () => {
        const urlParams = new URLSearchParams();
        urlParams.append('sort_by', '+average_daily_density_amount');
        urlParams.append('page_number', '1');
        urlParams.append('size', '5');

        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/areas?${urlParams}`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkAreas>;

            return data;
        } catch (error) {
            console.error('Unable to fetch bottom density areas', error);
            toast.error('Unable to fetch bottom density areas');
            throw error;
        }
    }
);

export const fetchTopDensityAreas = createAsyncThunk(
    'areaHealth/fetchTopDensityAreas',
    async () => {
        const urlParams = new URLSearchParams();
        urlParams.append('sort_by', '-average_daily_density_amount');
        urlParams.append('page_number', '1');
        urlParams.append('size', '5');

        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/areas?${urlParams}`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkAreas>;

            return data;
        } catch (error) {
            console.error('Unable to fetch top density areas', error);
            toast.error('Unable to fetch top density areas');
            throw error;
        }
    }
);

export const fetchBottomEmptyMilesAreas = createAsyncThunk(
    'areaHealth/fetchBottomEmptyMilesAreas',
    async () => {
        const urlParams = new URLSearchParams();
        urlParams.append('sort_by', '-average_empty_miles_percentage_amount');
        urlParams.append('page_number', '1');
        urlParams.append('size', '5');

        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/areas?${urlParams}`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<NetworkAreas>;

            return data;
        } catch (error) {
            console.error('Unable to fetch bottom empty miles areas', error);
            toast.error('Unable to fetch bottom empty miles areas');
            throw error;
        }
    }
);

export const areaHealthSlice = createSlice({
    name: 'areaHealth',
    initialState,
    reducers: {
        updateNetworkAreaHealthApiParams: (state, action: PayloadAction<GenericSortPaginationRequest>) => {
            state.networkAreaHealthApiParams = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchNetworkAreaHealth.pending, (state) => {
            state.networkAreaHealthApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchNetworkAreaHealth.fulfilled, (state, action) => {
            state.networkAreaHealthApiStatus = ApiStatus.Success;
            state.networkAreaHealth = action.payload;
        });
        builder.addCase(fetchNetworkAreaHealth.rejected, (state) => {
            state.networkAreaHealthApiStatus = ApiStatus.Failure;
            state.networkAreaHealth = initialState.networkAreaHealth;
        });

        builder.addCase(fetchBottomBalanceAreas.pending, (state) => {
            state.bottomBalanceAreasApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchBottomBalanceAreas.fulfilled, (state, action) => {
            state.bottomBalanceAreasApiStatus = ApiStatus.Success;
            state.bottomBalanceAreas = action.payload;
        });
        builder.addCase(fetchBottomBalanceAreas.rejected, (state) => {
            state.bottomBalanceAreasApiStatus = ApiStatus.Failure;
            state.bottomBalanceAreas = initialState.bottomBalanceAreas;
        });

        builder.addCase(fetchTopBalanceAreas.pending, (state) => {
            state.topBalanceAreasApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchTopBalanceAreas.fulfilled, (state, action) => {
            state.topBalanceAreasApiStatus = ApiStatus.Success;
            state.topBalanceAreas = action.payload;
        });
        builder.addCase(fetchTopBalanceAreas.rejected, (state) => {
            state.topBalanceAreasApiStatus = ApiStatus.Failure;
            state.topBalanceAreas = initialState.topBalanceAreas;
        });

        builder.addCase(fetchBottomDensityAreas.pending, (state) => {
            state.bottomDensityAreasApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchBottomDensityAreas.fulfilled, (state, action) => {
            state.bottomDensityAreasApiStatus = ApiStatus.Success;
            state.bottomDensityAreas = action.payload;
        });
        builder.addCase(fetchBottomDensityAreas.rejected, (state) => {
            state.bottomDensityAreasApiStatus = ApiStatus.Failure;
            state.bottomDensityAreas = initialState.bottomDensityAreas;
        });

        builder.addCase(fetchTopDensityAreas.pending, (state) => {
            state.topDensityAreasApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchTopDensityAreas.fulfilled, (state, action) => {
            state.topDensityAreasApiStatus = ApiStatus.Success;
            state.topDensityAreas = action.payload;
        });
        builder.addCase(fetchTopDensityAreas.rejected, (state) => {
            state.topDensityAreasApiStatus = ApiStatus.Failure;
            state.topDensityAreas = initialState.topDensityAreas;
        });

        builder.addCase(fetchBottomEmptyMilesAreas.pending, (state) => {
            state.bottomEmptyMilesAreasApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchBottomEmptyMilesAreas.fulfilled, (state, action) => {
            state.bottomEmptyMilesAreasApiStatus = ApiStatus.Success;
            state.bottomEmptyMilesAreas = action.payload;
        });
        builder.addCase(fetchBottomEmptyMilesAreas.rejected, (state) => {
            state.bottomEmptyMilesAreasApiStatus = ApiStatus.Failure;
            state.bottomEmptyMilesAreas = initialState.bottomEmptyMilesAreas;
        });
    },
});

export const {
    updateNetworkAreaHealthApiParams,
} = areaHealthSlice.actions;
export default areaHealthSlice.reducer;
